import React from 'react';
import { NodeProps } from '@xyflow/react';
import { useWorkflowStore } from '@/lib/workflows/store';

import useLocale from 'next-translate/useTranslation';

import { SimpleTooltip } from '@/components/ui/tooltip';

const GROUP_COLORS = [
  {
    id: 'blue',
    border: '#3b82f6',
    borderDark: '#60a5fa',
    bg: 'rgba(59,130,246,0.08)',
    bgSelected: 'rgba(59,130,246,0.15)',
    bgDark: 'rgba(59,130,246,0.1)',
    bgSelectedDark: 'rgba(59,130,246,0.2)',
    icon: '#3b82f6',
    iconDark: '#60a5fa',
    minimap: '#60a5fa',
  },
  {
    id: 'purple',
    border: '#8b5cf6',
    borderDark: '#a78bfa',
    bg: 'rgba(139,92,246,0.08)',
    bgSelected: 'rgba(139,92,246,0.15)',
    bgDark: 'rgba(139,92,246,0.1)',
    bgSelectedDark: 'rgba(139,92,246,0.2)',
    icon: '#8b5cf6',
    iconDark: '#a78bfa',
    minimap: '#a78bfa',
  },
  {
    id: 'green',
    border: '#10b981',
    borderDark: '#34d399',
    bg: 'rgba(16,185,129,0.08)',
    bgSelected: 'rgba(16,185,129,0.15)',
    bgDark: 'rgba(16,185,129,0.1)',
    bgSelectedDark: 'rgba(16,185,129,0.2)',
    icon: '#10b981',
    iconDark: '#34d399',
    minimap: '#34d399',
  },
  {
    id: 'orange',
    border: '#f59e0b',
    borderDark: '#fbbf24',
    bg: 'rgba(245,158,11,0.08)',
    bgSelected: 'rgba(245,158,11,0.15)',
    bgDark: 'rgba(245,158,11,0.1)',
    bgSelectedDark: 'rgba(245,158,11,0.2)',
    icon: '#f59e0b',
    iconDark: '#fbbf24',
    minimap: '#fbbf24',
  },
  {
    id: 'red',
    border: '#ef4444',
    borderDark: '#f87171',
    bg: 'rgba(239,68,68,0.08)',
    bgSelected: 'rgba(239,68,68,0.15)',
    bgDark: 'rgba(239,68,68,0.1)',
    bgSelectedDark: 'rgba(239,68,68,0.2)',
    icon: '#ef4444',
    iconDark: '#f87171',
    minimap: '#f87171',
  },
  {
    id: 'cyan',
    border: '#06b6d4',
    borderDark: '#22d3ee',
    bg: 'rgba(6,182,212,0.08)',
    bgSelected: 'rgba(6,182,212,0.15)',
    bgDark: 'rgba(6,182,212,0.1)',
    bgSelectedDark: 'rgba(6,182,212,0.2)',
    icon: '#06b6d4',
    iconDark: '#22d3ee',
    minimap: '#22d3ee',
  },
  {
    id: 'pink',
    border: '#ec4899',
    borderDark: '#f472b6',
    bg: 'rgba(236,72,153,0.08)',
    bgSelected: 'rgba(236,72,153,0.15)',
    bgDark: 'rgba(236,72,153,0.1)',
    bgSelectedDark: 'rgba(236,72,153,0.2)',
    icon: '#ec4899',
    iconDark: '#f472b6',
    minimap: '#f472b6',
  },
  {
    id: 'gray',
    border: '#6b7280',
    borderDark: '#9ca3af',
    bg: 'rgba(107,114,128,0.08)',
    bgSelected: 'rgba(107,114,128,0.15)',
    bgDark: 'rgba(107,114,128,0.1)',
    bgSelectedDark: 'rgba(107,114,128,0.2)',
    icon: '#6b7280',
    iconDark: '#9ca3af',
    minimap: '#9ca3af',
  },
] as const;

export type GroupColorId = (typeof GROUP_COLORS)[number]['id'];

export function getGroupColor(colorId?: string) {
  return GROUP_COLORS.find((c) => c.id === colorId) ?? GROUP_COLORS[0];
}

export { GROUP_COLORS };

interface GroupNodeProps extends NodeProps {
  readOnly?: boolean;
}

export function GroupNode({ id, data, selected, readOnly }: GroupNodeProps) {
  const { t } = useLocale('common');
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const ungroupNodes = useWorkflowStore((state) => state.ungroupNodes);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);

  const [isHovered, setIsHovered] = React.useState(false);
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [editedTitle, setEditedTitle] = React.useState('');
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const colorPickerRef = React.useRef<HTMLDivElement>(null);

  const label =
    typeof (data as { label?: unknown })?.label === 'string'
      ? ((data as { label?: string }).label as string)
      : t('Group');

  const colorId = (data as { color?: string })?.color;
  const locked = !!(data as { locked?: boolean })?.locked;
  const color = getGroupColor(colorId);

  const style = (data as { style?: { width?: number; height?: number } })
    ?.style;
  const width = style?.width ?? 400;
  const height = style?.height ?? 300;

  const isDark =
    typeof window !== 'undefined' &&
    document.documentElement.classList.contains('dark');

  const handleUngroup = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly || locked) return;
    ungroupNodes(id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    if (
      confirm(
        t('Delete group "{{label}}"? Children will be ungrouped.', { label }),
      )
    ) {
      deleteNode(id);
    }
  };

  const handleToggleLock = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    updateNodeData(id, { locked: !locked });
  };

  const handleColorChange = (newColorId: string) => {
    if (readOnly || locked) return;
    updateNodeData(id, { color: newColorId });
    setShowColorPicker(false);
  };

  const handleTitleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly || locked) return;
    setIsEditingTitle(true);
    setEditedTitle(label);
  };

  const handleTitleBlur = () => {
    if (readOnly) {
      setIsEditingTitle(false);
      return;
    }
    if (editedTitle.trim() && editedTitle !== label) {
      updateNodeData(id, { label: editedTitle.trim() });
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setEditedTitle(label);
      setIsEditingTitle(false);
    }
  };

  React.useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingTitle]);

  React.useEffect(() => {
    setEditedTitle(label);
  }, [label]);

  // Close color picker on outside click
  React.useEffect(() => {
    if (!showColorPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target as HTMLElement)
      ) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker]);

  return (
    <div
      style={{
        width,
        height,
        borderColor: isDark ? color.borderDark : color.border,
        backgroundColor: selected
          ? isDark
            ? color.bgSelectedDark
            : color.bgSelected
          : isDark
            ? color.bgDark
            : color.bg,
      }}
      className={`relative rounded-xl border-2 border-dashed transition-all duration-200 ${locked ? 'nodrag' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Lock badge */}
      {locked && (
        <div className='absolute top-2 right-2 z-10'>
          <svg
            className='w-4 h-4 text-gray-400 dark:text-gray-500'
            fill='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
          </svg>
        </div>
      )}

      {/* Label bar at top */}
      <div className='absolute -top-8 left-0 flex items-center gap-2 px-2 py-1 bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 z-10'>
        {/* Color dot - click to open picker */}
        <div className='relative' ref={colorPickerRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!readOnly && !locked) setShowColorPicker(!showColorPicker);
            }}
            className='w-4 h-4 rounded-full flex-shrink-0 border border-gray-300 dark:border-gray-600 cursor-pointer hover:scale-110 transition-transform nodrag'
            style={{
              backgroundColor: isDark ? color.borderDark : color.border,
            }}
            title={t('Change color')}
          />
          {/* Color picker popover */}
          {showColorPicker && (
            <div
              className='absolute top-full left-0 mt-1 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 flex gap-1.5 z-50 nodrag'
              onClick={(e) => e.stopPropagation()}
            >
              {GROUP_COLORS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleColorChange(c.id)}
                  className={`w-5 h-5 rounded-full border-2 hover:scale-110 transition-transform ${
                    c.id === (colorId ?? 'blue')
                      ? 'border-gray-900 dark:border-white scale-110'
                      : 'border-transparent'
                  }`}
                  style={{ backgroundColor: isDark ? c.borderDark : c.border }}
                  title={c.id}
                />
              ))}
            </div>
          )}
        </div>
        {isEditingTitle ? (
          <input
            ref={inputRef}
            type='text'
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className='font-medium text-xs text-gray-900 dark:text-white bg-transparent border-b border-blue-500 dark:border-blue-400 outline-none px-1 min-w-[100px] nodrag'
          />
        ) : (
          <div
            className='font-medium text-xs text-gray-900 dark:text-white whitespace-nowrap cursor-text'
            onDoubleClick={handleTitleDoubleClick}
            title={locked ? t('Unlock to edit') : t('Double-click to edit')}
          >
            {label}
          </div>
        )}
      </div>

      {/* Action Toolbar */}
      {(selected || isHovered) && !readOnly && (
        <div className='absolute -top-8 right-0 flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 z-10'>
          <SimpleTooltip
            title={locked ? t('Unlock group') : t('Lock group')}
            side='top'
          >
            <button
              onClick={handleToggleLock}
              className={`flex-shrink-0 p-1.5 rounded transition-colors ${
                locked
                  ? 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {locked ? (
                <svg
                  className='w-4 h-4 text-amber-600 dark:text-amber-400'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
                </svg>
              ) : (
                <svg
                  className='w-4 h-4 text-gray-500 dark:text-gray-400'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z' />
                </svg>
              )}
            </button>
          </SimpleTooltip>
          <SimpleTooltip
            title={locked ? t('Unlock to ungroup') : t('Ungroup nodes')}
            side='top'
          >
            <button
              onClick={handleUngroup}
              disabled={locked}
              className={`flex-shrink-0 p-1.5 rounded transition-colors ${
                locked
                  ? 'opacity-40 cursor-not-allowed'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <svg
                className='w-4 h-4 text-blue-600 dark:text-blue-400'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path d='M8 7h12m0 0l-4-4m4 4l-4 4m0 5H4m0 0l4 4m-4-4l4-4' />
              </svg>
            </button>
          </SimpleTooltip>
          <SimpleTooltip title={t('Delete group')} side='top'>
            <button
              onClick={handleDelete}
              className='flex-shrink-0 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors'
            >
              <svg
                className='w-4 h-4 text-red-600 dark:text-red-400'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
              </svg>
            </button>
          </SimpleTooltip>
        </div>
      )}
    </div>
  );
}
